import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Layout } from '../components/Layout';
import { FadeInUp, StaggerContainer, StaggerItem } from '../components/AnimatedSection';
import { ExternalLink, Scale, FileText, Shield, AlertTriangle, MapPin, Rocket, GraduationCap, Building2 } from 'lucide-react';

const Legislation = () => {
  const { t } = useLanguage();

  return (
    <Layout>
      {/* Hero */}
      <section className="py-24 bg-white" data-testid="legislation-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <span className="confidential-badge mb-6 inline-block">
              {t('legislation.badge')}
            </span>
          </FadeInUp>
          
          <FadeInUp delay={0.1}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy mb-6" data-testid="legislation-title">
              {t('legislation.title')}
            </h1>
          </FadeInUp>
          
          <FadeInUp delay={0.2}>
            <p className="text-lg md:text-xl text-[#0066CC] font-medium max-w-3xl">
              {t('legislation.subtitle')}
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* National Legislation */}
      <section className="py-20 bg-slate-50" data-testid="legislation-national">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <div className="flex items-center gap-3 mb-8">
              <Scale className="w-6 h-6 text-[#0066CC]" />
              <h2 className="text-2xl md:text-3xl font-bold text-navy">
                {t('legislation.national.title')}
              </h2>
            </div>
          </FadeInUp>

          <StaggerContainer className="grid md:grid-cols-2 gap-6" staggerDelay={0.1}>
            <StaggerItem>
              <div className="bg-white border border-slate-200 p-8 hover:border-[#0066CC] transition-colors">
                <FileText className="w-8 h-8 text-slate-400 mb-4" />
                <h3 className="text-lg font-semibold text-navy mb-2">
                  {t('legislation.national.law1.title')}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {t('legislation.national.law1.desc')}
                </p>
              </div>
            </StaggerItem>
            
            <StaggerItem>
              <div className="bg-white border border-slate-200 p-8 hover:border-[#0066CC] transition-colors">
                <FileText className="w-8 h-8 text-slate-400 mb-4" />
                <h3 className="text-lg font-semibold text-navy mb-2">
                  {t('legislation.national.law2.title')}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {t('legislation.national.law2.desc')}
                </p>
              </div>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* Romania Strategy - Where is Romania now? */}
      <section className="py-20 bg-white" data-testid="legislation-strategy">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-6 h-6 text-[#0066CC]" />
              <h2 className="text-2xl md:text-3xl font-bold text-navy">
                {t('legislation.strategy.title')}
              </h2>
            </div>
            <p className="text-lg text-[#0066CC] font-medium mb-4">
              {t('legislation.strategy.subtitle')}
            </p>
            <p className="text-slate-600 mb-10 max-w-4xl">
              {t('legislation.strategy.intro')}
            </p>
          </FadeInUp>

          {/* Official Documents */}
          <StaggerContainer className="grid md:grid-cols-3 gap-6 mb-12" staggerDelay={0.1}>
            <StaggerItem>
              <div className="bg-slate-50 border border-slate-200 p-6 hover:border-[#0066CC] transition-colors h-full flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-[#0066CC]" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#0066CC]">Document Oficial</span>
                </div>
                <h3 className="text-base font-semibold text-navy mb-3">
                  {t('legislation.strategy.docs.sncisi.title')}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-grow">
                  {t('legislation.strategy.docs.sncisi.desc')}
                </p>
                <a
                  href={t('legislation.strategy.docs.sncisi.link')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0066CC] hover:text-navy transition-colors"
                >
                  {t('legislation.strategy.docs.sncisi.linkText')} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </StaggerItem>
            
            <StaggerItem>
              <div className="bg-slate-50 border border-slate-200 p-6 hover:border-[#0066CC] transition-colors h-full flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-[#0066CC]" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#0066CC]">Prezentare</span>
                </div>
                <h3 className="text-base font-semibold text-navy mb-3">
                  {t('legislation.strategy.docs.presentation.title')}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-grow">
                  {t('legislation.strategy.docs.presentation.desc')}
                </p>
                <a
                  href={t('legislation.strategy.docs.presentation.link')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0066CC] hover:text-navy transition-colors"
                >
                  {t('legislation.strategy.docs.presentation.linkText')} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </StaggerItem>
            
            <StaggerItem>
              <div className="bg-slate-50 border border-slate-200 p-6 hover:border-[#0066CC] transition-colors h-full flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="w-5 h-5 text-[#0066CC]" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#0066CC]">Rapoarte</span>
                </div>
                <h3 className="text-base font-semibold text-navy mb-3">
                  {t('legislation.strategy.docs.uefiscdi.title')}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-grow">
                  {t('legislation.strategy.docs.uefiscdi.desc')}
                </p>
                <a
                  href={t('legislation.strategy.docs.uefiscdi.link')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0066CC] hover:text-navy transition-colors"
                >
                  {t('legislation.strategy.docs.uefiscdi.linkText')} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </StaggerItem>
          </StaggerContainer>

          {/* Initiatives and Projects */}
          <FadeInUp delay={0.3}>
            <h3 className="text-xl font-bold text-navy mb-6 flex items-center gap-2">
              <Rocket className="w-5 h-5 text-[#0066CC]" />
              {t('legislation.strategy.initiatives.title')}
            </h3>
          </FadeInUp>

          <StaggerContainer className="grid md:grid-cols-2 gap-6" staggerDelay={0.1}>
            <StaggerItem>
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 p-6 hover:border-[#0066CC] transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">🛰️</span>
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">ESA</span>
                </div>
                <h4 className="text-base font-semibold text-navy mb-2">
                  {t('legislation.strategy.initiatives.esa.title')}
                </h4>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  {t('legislation.strategy.initiatives.esa.desc')}
                </p>
                <a
                  href={t('legislation.strategy.initiatives.esa.link')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0066CC] hover:text-navy transition-colors"
                >
                  {t('legislation.strategy.initiatives.esa.linkText')} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </StaggerItem>
            
            <StaggerItem>
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 p-6 hover:border-[#0066CC] transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">🧪</span>
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">EU Project</span>
                </div>
                <h4 className="text-base font-semibold text-navy mb-2">
                  {t('legislation.strategy.initiatives.siramm.title')}
                </h4>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  {t('legislation.strategy.initiatives.siramm.desc')}
                </p>
                <a
                  href={t('legislation.strategy.initiatives.siramm.link')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0066CC] hover:text-navy transition-colors"
                >
                  {t('legislation.strategy.initiatives.siramm.linkText')} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* International Standards */}
      <section className="py-20 bg-white" data-testid="legislation-international">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <div className="flex items-center gap-3 mb-8">
              <Shield className="w-6 h-6 text-[#0066CC]" />
              <h2 className="text-2xl md:text-3xl font-bold text-navy">
                {t('legislation.international.title')}
              </h2>
            </div>
          </FadeInUp>

          <StaggerContainer className="grid md:grid-cols-2 gap-6" staggerDelay={0.1}>
            <StaggerItem>
              <div className="bg-slate-50 border border-slate-200 p-8 hover:border-[#0066CC] transition-colors">
                <h3 className="text-lg font-semibold text-navy mb-2">
                  {t('legislation.international.iso.title')}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  {t('legislation.international.iso.desc')}
                </p>
                <a
                  href="https://www.iso.org/standard/74514.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0066CC] hover:text-navy transition-colors"
                >
                  iso.org <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </StaggerItem>
            
            <StaggerItem>
              <div className="bg-slate-50 border border-slate-200 p-8 hover:border-[#0066CC] transition-colors">
                <h3 className="text-lg font-semibold text-navy mb-2">
                  {t('legislation.international.edf.title')}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  {t('legislation.international.edf.desc')}
                </p>
                <a
                  href="https://defence-industry-space.ec.europa.eu/eu-defence-industry/european-defence-fund-edf_en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0066CC] hover:text-navy transition-colors"
                >
                  ec.europa.eu <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* Export Control */}
      <section className="py-20 bg-slate-50" data-testid="legislation-control">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <div className="flex items-center gap-3 mb-8">
              <AlertTriangle className="w-6 h-6 text-[#0066CC]" />
              <h2 className="text-2xl md:text-3xl font-bold text-navy">
                {t('legislation.control.title')}
              </h2>
            </div>
          </FadeInUp>

          <StaggerContainer className="grid md:grid-cols-2 gap-6" staggerDelay={0.1}>
            <StaggerItem>
              <div className="bg-white border border-slate-200 p-8 hover:border-[#0066CC] transition-colors">
                <h3 className="text-lg font-semibold text-navy mb-2">
                  {t('legislation.control.itar.title')}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  {t('legislation.control.itar.desc')}
                </p>
                <a
                  href="https://www.pmddtc.state.gov/ddtc_public?id=ddtc_public_portal_itar_landing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0066CC] hover:text-navy transition-colors"
                >
                  state.gov <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </StaggerItem>
            
            <StaggerItem>
              <div className="bg-white border border-slate-200 p-8 hover:border-[#0066CC] transition-colors">
                <h3 className="text-lg font-semibold text-navy mb-2">
                  {t('legislation.control.dual.title')}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  {t('legislation.control.dual.desc')}
                </p>
                <a
                  href="https://policy.trade.ec.europa.eu/help-exporters-and-importers/exporting-dual-use-items_en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0066CC] hover:text-navy transition-colors"
                >
                  trade.ec.europa.eu <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* Strategic Note */}
      <section className="py-20 bg-navy text-white" data-testid="legislation-note">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <div className="max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">
                {t('legislation.note.title')}
              </h2>
              <p className="text-white/80 text-base md:text-lg leading-relaxed">
                {t('legislation.note.text')}
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>
    </Layout>
  );
};

export default Legislation;
